import React, { useState, useEffect, useRef } from "react";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Popper from "@material-ui/core/Popper";
import Fade from "@material-ui/core/Fade";
import { makeStyles } from "@material-ui/core/styles";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import { useLocation } from "react-router-dom";

var HINT_KEY = "onboarding_hint";

var HINTS = {
  connection: {
    selector: "[data-onboarding='connection']",
    title: "Criar conexao WhatsApp",
    text: "Clique aqui para adicionar uma nova conexao WhatsApp. Voce podera escolher entre Baileys (QR Code) ou Cloud API (numero oficial Meta).",
    placement: "bottom",
  },
  queue: {
    selector: "[data-onboarding='queue']",
    title: "Criar setor de atendimento",
    text: "Clique aqui para criar seu primeiro setor (fila). Setores organizam os atendimentos por area, ex: Vendas, Suporte, Financeiro.",
    placement: "bottom",
  },
  user: {
    selector: "[data-onboarding='user']",
    title: "Adicionar atendente",
    text: "Clique aqui para criar um usuario atendente. Depois de criado, vincule-o a um setor para que ele receba tickets.",
    placement: "bottom",
  },
  connected: {
    selector: null,
    title: "Conectar o WhatsApp",
    text: "Na lista de conexoes, clique em 'QR Code' ao lado da sua conexao e escaneie com o celular. Aguarde o status ficar CONNECTED.",
  },
  greeting: {
    selector: null,
    title: "Mensagem de saudacao",
    text: "Va em Configuracoes > aba Geral e preencha o campo 'Mensagem de saudacao'. Ela e enviada automaticamente quando um cliente inicia o atendimento.",
  },
  farewell: {
    selector: null,
    title: "Mensagem de encerramento",
    text: "Va em Configuracoes > aba Geral e preencha o campo 'Mensagem de encerramento'. Ela e enviada ao fechar o ticket.",
  },
  message: {
    selector: null,
    title: "Enviar primeira mensagem",
    text: "Clique em um ticket existente ou use o botao '+' para iniciar um novo atendimento e enviar a primeira mensagem de teste.",
  },
  tickets_main: {
    selector: null,
    title: "Configuracao concluida!",
    text: "Parabens! Sua conta esta pronta. Esta e a tela de atendimento: 'Aguardando' sao tickets novos, 'Atendendo' esta com um agente, 'Resolvidos' ja foram encerrados. Use o botao '+' para iniciar um atendimento manual.",
  },
};

var useStyles = makeStyles(function(theme) {
  return {
    popper: {
      zIndex: 2000,
      maxWidth: 320,
    },
    paper: {
      padding: theme.spacing(2),
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
      borderRadius: 8,
      boxShadow: theme.shadows[8],
      position: "relative",
    },
    arrow: {
      position: "absolute",
      top: -16,
      left: "50%",
      transform: "translateX(-50%)",
      color: theme.palette.primary.main,
    },
    title: {
      fontWeight: 700,
      marginBottom: theme.spacing(0.5),
      fontSize: "0.95rem",
    },
    text: {
      fontSize: "0.82rem",
      lineHeight: 1.5,
      marginBottom: theme.spacing(1.5),
      opacity: 0.95,
    },
    dismiss: {
      color: "#fff",
      borderColor: "rgba(255,255,255,0.7)",
      fontSize: "0.75rem",
      padding: "2px 12px",
    },
    banner: {
      position: "fixed",
      top: 64,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 2000,
      minWidth: 320,
      maxWidth: 440,
      padding: theme.spacing(2),
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
      borderRadius: 8,
      boxShadow: theme.shadows[8],
    },
  };
});

function OnboardingTour() {
  var classes = useStyles();
  var location = useLocation();

  var anchorState = useState(null);
  var anchor = anchorState[0];
  var setAnchor = anchorState[1];

  var hintState = useState(null);
  var hint = hintState[0];
  var setHint = hintState[1];

  var timerRef = useRef(null);

  useEffect(function() {
    var key = sessionStorage.getItem(HINT_KEY);
    if (!key || !HINTS[key]) {
      setHint(null);
      setAnchor(null);
      return;
    }
    if (key === "tickets_main" && location.pathname !== "/" && !location.pathname.startsWith("/tickets")) {
      return;
    }
    var cfg = HINTS[key];
    setHint(cfg);

    if (cfg.selector) {
      // Wait for DOM to render the target element
      timerRef.current = setTimeout(function() {
        var el = document.querySelector(cfg.selector);
        if (el) {
          setAnchor(el);
          el.style.outline = "3px solid #fff";
          el.style.outlineOffset = "3px";
          el.style.borderRadius = "4px";
        } else {
          setAnchor(null);
        }
      }, 600);
    } else {
      setAnchor(null);
    }

    return function() {
      clearTimeout(timerRef.current);
    };
  }, [location.pathname]);

  function dismiss() {
    sessionStorage.removeItem(HINT_KEY);
    // Remove outline from anchored element
    if (anchor) {
      anchor.style.outline = "";
      anchor.style.outlineOffset = "";
    }
    setHint(null);
    setAnchor(null);
  }

  if (!hint) return null;

  // Anchored popper (for connection, queue, user)
  if (anchor) {
    return React.createElement(
      Popper,
      { open: true, anchorEl: anchor, placement: "bottom", transition: true, className: classes.popper },
      function(popperProps) {
        return React.createElement(
          Fade,
          { in: popperProps.TransitionProps ? popperProps.TransitionProps.in : true, timeout: 350 },
          React.createElement(
            "div",
            null,
            React.createElement(
              Paper,
              { className: classes.paper },
              React.createElement(ArrowUpwardIcon, { className: classes.arrow, fontSize: "small" }),
              React.createElement(Typography, { className: classes.title }, hint.title),
              React.createElement(Typography, { className: classes.text }, hint.text),
              React.createElement(
                Button,
                { variant: "outlined", size: "small", className: classes.dismiss, onClick: dismiss },
                "Entendido"
              )
            )
          )
        );
      }
    );
  }

  // Banner (for steps without a specific element target)
  return React.createElement(
    Paper,
    { className: classes.banner, elevation: 8 },
    React.createElement(Typography, { className: classes.title }, hint.title),
    React.createElement(Typography, { className: classes.text }, hint.text),
    React.createElement(
      Button,
      { variant: "outlined", size: "small", className: classes.dismiss, onClick: dismiss },
      "Entendido"
    )
  );
}

export default OnboardingTour;
